import { Fragment, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import {
  Dialog,
  MenuItem,
  Transition,
  TransitionChild,
} from "@headlessui/react";

import useLocalStorage from "hooks/useLocalStorage";

import { OptionsMenu } from "@catchy/ui";
import {
  formatStopDistance,
  getPreferredStopName,
  orderStopsWithNearbyFirst,
  useNearbyFirst,
} from "@catchy/app-core";
import StopRow from "./StopRow";
import ReorderFavourites from "./ReorderFavourites";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import Star from "@catchy/assets/svgs/star.svg";

const Favourites = ({
  isApple,
  isClient,
  isLoading,
  preferredStopNames,
  stops,
  type,
}) => {
  const router = useRouter();
  const [favourites] = useLocalStorage("favourites", []);
  const {
    canShowOption: showNearbyOption,
    isNearbyFirstActive,
    requestNearbyFirst,
    showNearbyFirst,
    userLocation,
  } = useNearbyFirst({
    storageKey: "showNearbyFavouritesFirst",
    useLocalStorage,
  });
  const favouritesList = Array.isArray(favourites) ? favourites : [];
  const reorderSaveHandlerRef = useRef(null);

  const getTypeFavourites = () =>
    favouritesList
      .filter((fav) => stops?.some((stop) => stop.stop_id === fav))
      .map((fav) => {
        const preferredName = getPreferredStopName(
          preferredStopNames,
          fav,
          fav,
        );
        const stop_name =
          preferredName ||
          stops.find((stop) => stop.stop_id === fav)?.stop_name;
        const stop_lat = stops.find((stop) => stop.stop_id === fav)?.stop_lat;
        const stop_lon = stops.find((stop) => stop.stop_id === fav)?.stop_lon;
        const stop_type = stops.find((stop) => stop.stop_id === fav)?.stop_type;
        return { stop_id: fav, stop_lat, stop_lon, stop_name, stop_type };
      });

  const typeFavourites = getTypeFavourites();
  const displayedFavourites =
    isNearbyFirstActive && userLocation
      ? orderStopsWithNearbyFirst({
          stops: typeFavourites,
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
        })
      : typeFavourites;

  const closeReorderModal = useCallback(async () => {
    if (reorderSaveHandlerRef.current) {
      await reorderSaveHandlerRef.current();
    }
    await router.replace(type === "bus" ? "/" : `/${type}`);
  }, [router, type]);
  return (
    <>
      <section className="flex flex-col">
        <div className="mb-2 flex justify-between px-[20px]">
          <h2 className="text-3xl font-semibold">Favourites</h2>
          {isClient &&
            !isLoading &&
            (typeFavourites.length > 1 || showNearbyOption) && (
              <OptionsMenu isApple={isApple}>
                {typeFavourites.length > 1 && (
                  <MenuItem>
                    <button
                      className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3"
                      onClick={() => {
                        router.push(
                          type === "bus" ? "/?reorder" : `/${type}/?reorder`,
                          type === "bus" ? "/reorder" : `/${type}/reorder`,
                        );
                      }}
                    >
                      Reorder Favourites…
                    </button>
                  </MenuItem>
                )}
                {showNearbyOption && (
                  <MenuItem>
                    <button
                      aria-pressed={showNearbyFirst}
                      className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3"
                      onClick={() => {
                        requestNearbyFirst(!showNearbyFirst);
                      }}
                    >
                      <span>Show nearby first</span>
                      <span className="opacity-60">
                        {showNearbyFirst ? "On" : "Off"}
                      </span>
                    </button>
                  </MenuItem>
                )}
              </OptionsMenu>
            )}
        </div>

        <div className="flex flex-col">
          {isLoading ? (
            <div className="mx-[20px] flex justify-start py-4">
              <div className="grid h-6 w-11 shrink-0 place-items-center pt-1">
                <Star className="h-5 w-5 text-zinc-400 opacity-50" />
              </div>
              <aside className="text-pretty pt-1 text-lg leading-tight opacity-80">
                Loading favourites…
              </aside>
            </div>
          ) : (
            isClient &&
            (typeFavourites.length ? (
              displayedFavourites.map((stop) => (
                <StopRow
                  stop_id={stop.stop_id}
                  stop_name={stop.stop_name}
                  stop_type={stop.stop_type}
                  type={type}
                  distance={
                    isNearbyFirstActive
                      ? formatStopDistance(stop.distanceMeters)
                      : null
                  }
                  key={stop.stop_id}
                />
              ))
            ) : (
              <div className="mx-[20px] flex justify-start py-4">
                <div className="grid h-6 w-11 shrink-0 place-items-center pt-1">
                  <Star className="h-5 w-5 text-zinc-400 opacity-50" />
                </div>
                <aside className="text-pretty pt-1 text-lg leading-tight opacity-80">
                  Add stops to your favourites by tapping the star icon next to
                  the stop name.
                </aside>
              </div>
            ))
          )}
        </div>
      </section>

      <Transition
        appear
        show={router.query.reorder !== undefined}
        as={Fragment}
      >
        <Dialog
          as="div"
          onClose={() => {
            void closeReorderModal();
          }}
          className="fixed inset-0 z-30 overflow-y-auto"
        >
          <div className="flex min-h-[var(--vvh)] items-center justify-center overflow-hidden">
            <TransitionChild
              as={Fragment}
              enter="transition-[transform,opacity] ease-out duration-200"
              enterFrom="opacity-0 translate-y-10"
              enterTo="opacity-100 translate-y-0"
              leave="transition-[transform,opacity] ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-5"
              className="absolute inset-0 overflow-y-auto bg-white opacity-100 motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:data-[closed]:opacity-100 motion-reduce:data-[closed]:translate-y-0 dark:bg-black"
            >
              <div>
                <div className="mx-auto flex w-full max-w-md flex-col px-[20px] py-5">
                  <div className="mb-2">
                    <button
                      className="pointer-events-auto grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none ring-blue-500/50 transition-colors duration-150 motion-reduce:transition-none hover:bg-zinc-400/10 focus-visible:ring active:bg-zinc-400/10"
                      onClick={() => {
                        void closeReorderModal();
                      }}
                    >
                      <CloseCross className="h-5 w-5" />
                    </button>
                  </div>
                  <h2 className="mb-4 text-3xl font-semibold">
                    Reorder Favourites
                  </h2>
                  <p className="mb-6 text-lg opacity-80">
                    Drag and drop stops to reorder them.
                  </p>
                  <ReorderFavourites
                    typeFavourites={typeFavourites}
                    onSaveOrderChange={(saveFn) => {
                      reorderSaveHandlerRef.current = saveFn;
                    }}
                    type={type}
                  />
                </div>
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Favourites;
